import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Printer } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { companyApi } from '../services/companyApi';
import { cashflowApi } from '../services/cashflowApi';
import { Vehicle, shortWon, won, KIND_LABEL } from '../types/vehicle';
import { Deal, Valuation } from '../types/deal';
import { Company } from '../types/company';
import { Cashflow, KIND_META } from '../types/cashflow';
import { computeMetrics, mult, pct } from '../lib/metrics';
import { missingProfile, missingTerms } from '../lib/vics';
import { Tour, TourButton, TourStep } from '../components/Tour';
import { CLOSING_TASKS, ClosingTask, closingApi } from '../services/vicsApi';
import { monthsAgo, todayLocal } from '../lib/date';

/**
 * 내부(대표) 보고 — 한 장짜리 현황.
 *
 * ⚠️ 고유자금과 위탁자금을 합치지 않는다. 「운용 규모」는 위탁자금, 「직접 투자」는 고유자금으로 따로 센다.
 */
export const InternalReportPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [values, setValues] = useState<Record<number, Valuation>>({});
  const [loading, setLoading] = useState(true);
  const [tourRun, setTourRun] = useState(false);

  // 지난 사업연도 결산 진행 — 기한(다음 해 3/31)이 지났는데 다 끝내지 않은 조합을 「확인 필요」에
  const lastYear = new Date().getFullYear() - 1;
  const [closing, setClosing] = useState<ClosingTask[]>([]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, d, c, f, lv, ct] = await Promise.all([vehicleApi.list(), dealApi.list(), companyApi.list(), cashflowApi.list(), dealApi.latestValues(), closingApi.list(lastYear).catch(() => [])]);
      setVehicles(v); setDeals(d); setCompanies(c); setFlows(f); setValues(lv); setClosing(ct);
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const today = todayLocal();
  const yearStart = `${today.slice(0, 4)}-01-01`;
  const cName = (id: number) => companies.find((c) => c.id === id)?.name ?? '—';
  const open = (d: Deal) => d.stage !== '회수' && d.stage !== '상각';

  // 평가 없는 보유 건이 있으면 TVPI · IRR 「—」 — 0 으로 세면 전액 손실처럼 보인다(대시보드와 같은 규칙)
  const metricsOf = (ids: Set<number>) => {
    const held = deals.filter((d) => ids.has(d.vehicleId) && open(d) && d.stage !== '검토');
    const res = held.reduce((s, d) => s + (values[d.id]?.ourValue ?? 0), 0);
    const m = computeMetrics(flows.filter((f) => ids.has(f.vehicleId)), res);
    return held.some((d) => !values[d.id]) ? { ...m, tvpi: null, irr: null } : m;
  };
  const byKind = (kind: 'own' | 'entrusted') => metricsOf(new Set(vehicles.filter((v) => v.kind === kind).map((v) => v.id)));
  const own = byKind('own');
  const ent = byKind('entrusted');
  const feeYtd = flows.filter((f) => f.kind === 'fee' && f.occurredOn >= yearStart).reduce((s, f) => s + f.amount, 0);
  const recent = flows.slice(0, 6);

  const checks = useMemo(() => {
    const out: { text: string; to: string }[] = [];
    vehicles.filter((v) => v.kind === 'entrusted' && !v.legalForm).forEach((v) => out.push({ text: `${v.name} — 법적 형태 확인 전`, to: `/vehicles/${v.id}` }));
    // VICS 는 조합만 낸다 — 다른 자금원의 건은 빈칸이 있어도 셀 이유가 없다
    const unions = new Set(vehicles.filter((v) => v.legalForm === '조합').map((v) => v.id));
    const staleBefore = monthsAgo(6);
    // 결산 기한 지남 — 조합만, 지난 사업연도
    const due = `${lastYear + 1}-03-31`;
    if (today > due) {
      vehicles.filter((v) => v.legalForm === '조합' && (v.registeredOn ?? v.startedOn ?? '') <= `${lastYear}-12-31`).forEach((v) => {
        const done = CLOSING_TASKS.filter((t) => closing.find((x) => x.vehicleId === v.id && x.task === t)?.doneAt).length;
        if (done < CLOSING_TASKS.length) {
          const days = Math.floor((new Date(today).getTime() - new Date(due).getTime()) / 86_400_000);
          out.push({ text: `${v.name} — ${lastYear} 결산 기한 ${days}일 지남 (${done}/${CLOSING_TASKS.length})`, to: '/reports/closing' });
        }
      });
    }
    // VICS 조합 코드 — 조합은 VICS 보고에 쓴다
    vehicles.filter((v) => v.legalForm === '조합' && !v.vicsCode).forEach((v) => out.push({ text: `${v.name} — VICS 조합 코드 없음`, to: `/vehicles/${v.id}` }));
    deals.filter(open).forEach((d) => {
      const last = values[d.id]?.valuedOn;
      if (!last) out.push({ text: `${cName(d.companyId)} — 평가 없음`, to: `/deals/${d.id}` });
      else if (last < staleBefore) out.push({ text: `${cName(d.companyId)} — 평가 6개월 넘음`, to: `/deals/${d.id}` });
      if (!unions.has(d.vehicleId)) return;
      const gap = missingTerms(d, false).length
        + missingProfile(companies.find((c) => c.id === d.companyId)).length;
      if (gap > 0) out.push({ text: `${cName(d.companyId)} — VICS 빈칸 ${gap}`, to: `/companies/${d.companyId}` });
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, deals, values, companies, closing]);

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'kpi', target: '[data-tour="kpi"]', title: '한 줄 요약', content: '위탁자금 운용 규모 · 고유자금 직접 투자 · 올해 GRK 매출. 둘을 합치지 않습니다.' },
    { name: 'checks', target: '[data-tour="checks"]', title: '확인 필요', content: '법적 형태 확인 전 · 결산 기한 지남 · VICS 조합 코드 · 평가 없음 · 평가가 오래된 건 · VICS 빈칸을 모았습니다. 누르면 그 화면으로 갑니다.' },
    { name: 'print', target: '[data-tour="print"]', title: '인쇄', content: '한 장으로 인쇄해 보고합니다.', placement: 'left' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-5 print:hidden">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">내부 보고 <TourButton onClick={() => setTourRun(true)} /></h1>
        <button data-tour="print" onClick={() => window.print()}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">
          <Printer size={12} /> 인쇄 · PDF
        </button>
      </div>

      <article className="bg-white border border-slate-200 rounded-lg px-10 py-9 space-y-7 print:border-0 print:px-0">
        <header className="border-b-2 border-slate-900 pb-3">
          <div className="text-xs text-slate-500">기준일 {today.replace(/-/g, '.')}</div>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">투자 현황 보고</h2>
        </header>

        <section data-tour="kpi" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="border border-slate-200 rounded px-4 py-3">
            <div className="text-xs text-slate-400">위탁자금 · 운용 규모 (출자)</div>
            <div className="text-xl font-bold tabular-nums">{shortWon(ent.contributed)}</div>
            <div className="text-xs text-slate-500">TVPI {mult(ent.tvpi)} · IRR {pct(ent.irr)}</div>
          </div>
          <div className="border border-slate-200 rounded px-4 py-3">
            <div className="text-xs text-slate-400">고유자금 · 직접 투자</div>
            <div className="text-xl font-bold tabular-nums">{shortWon(own.invested)}</div>
            <div className="text-xs text-slate-500">TVPI {mult(own.tvpi)} · IRR {pct(own.irr)}</div>
          </div>
          <div className="border border-amber-200 bg-amber-50/40 rounded px-4 py-3">
            <div className="text-xs text-slate-400">GRK 매출 · 올해 보수</div>
            <div className="text-xl font-bold tabular-nums text-amber-700">{shortWon(feeYtd)}</div>
            <div className="text-xs text-slate-500">누적 {shortWon(own.fee + ent.fee)}</div>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-bold text-slate-900 mb-2">자금원별</h3>
          <table className="w-full text-sm border-t border-slate-300">
            <thead><tr className="text-xs text-slate-500 border-b border-slate-200">
              <th className="text-left font-medium py-1.5">자금원</th><th className="text-right font-medium">투자</th><th className="text-right font-medium">회수</th>
              <th className="text-right font-medium">TVPI</th><th className="text-right font-medium">IRR</th>
            </tr></thead>
            <tbody>
              {vehicles.map((v) => {
                const m = metricsOf(new Set([v.id]));
                return (
                  <tr key={v.id} className="border-b border-slate-100">
                    <td className="py-1.5">{v.name} <span className="text-xs text-slate-400">{KIND_LABEL[v.kind]}</span></td>
                    <td className="text-right tabular-nums">{shortWon(m.invested)}</td><td className="text-right tabular-nums">{shortWon(m.returned)}</td>
                    <td className="text-right tabular-nums">{mult(m.tvpi)}</td><td className="text-right tabular-nums">{pct(m.irr)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <div className="grid gap-6 sm:grid-cols-2">
          <section>
            <h3 className="text-sm font-bold text-slate-900 mb-2">최근 원장</h3>
            <ul className="text-sm divide-y divide-slate-100 border-t border-slate-300">
              {recent.map((f) => (
                <li key={f.id} className="flex justify-between gap-2 py-1.5">
                  <span className="text-slate-600 tabular-nums">{f.occurredOn.replace(/-/g, '.')} {KIND_META[f.kind].label}</span>
                  <span className="tabular-nums">{f.direction === 'in' ? '+' : '−'}{won(f.amount).slice(1)}</span>
                </li>
              ))}
            </ul>
          </section>
          <section data-tour="checks">
            <h3 className="text-sm font-bold text-slate-900 mb-2">확인 필요 {checks.length}</h3>
            <ul className="text-sm divide-y divide-slate-100 border-t border-slate-300">
              {checks.map((c, i) => (
                <li key={`${i}-${c.text}`} className="py-1.5"><Link to={c.to} className="text-amber-800 hover:underline">{c.text}</Link></li>
              ))}
            </ul>
          </section>
        </div>
      </article>

      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
